import type { TagTone } from '@dayon/design-system';
import { DOCUMENT_CONFIDENCE_KEY, type IdentityRecordFacts } from '@dayon/contracts';
import { codeAsWords } from '../../lib/audit-words.ts';

/**
 * HOW ONE IDENTITY CHECK WAS DECIDED, in words — for the player page's KYC tab and for a
 * decided case on Identity checks, which describe the same record and must not describe it two
 * ways. Staging, 15 Oct: once a check was decided no screen showed what was decided, so "how
 * was this player verified?" could be answered only by a database query.
 *
 * Every sentence here is conditioned on a value the route sent. Nothing is filled in.
 */

/**
 * ONE IDENTITY RECORD AS `GET /api/kyc/identity/players/:playerId` SENDS IT — the contract's own
 * type (@dayon/contracts, `identityRecordFacts`), not a copy typed here by hand. A decided case on
 * Identity checks is drawn from the same shape (`factsOfCase`).
 *
 * `noCiraCheckOnRecord` is sent by the route — never the reference it is computed from. CIRA
 * refused the submission, an image was gone, or the platform could not record the reference CIRA
 * gave: such a check cannot be approved, and in the last cause CIRA did receive the documents, so
 * what it lacks is said as not on record — as the case says it.
 */
export type { IdentityRecordFacts } from '@dayon/contracts';

/**
 * Where the name or the date of birth on a record came from, as the identity routes decide it
 * (`identitySources`): the service's reading, the reviewer's typing, or an approval from before
 * the platform recorded which.
 */
export type IdentitySource = IdentityRecordFacts['sources']['fullName'];

/** Who decided a check, when, and what they wrote — null while nobody has. A refusal's reason is
    already in words. */
export type RecordDecision = NonNullable<IdentityRecordFacts['decision']>;

/**
 * CIRA's sanctions and PEP screening of one check: an answer per list, and its overall verdict.
 * NULL on a list means CIRA DID NOT SCREEN it — never "no match". Since 15 Oct 2026, 8:22 PM
 * CIRA screens nobody on this account, so every new check reads that way.
 */
export type ScreeningFacts = IdentityRecordFacts['screening'];

/**
 * CIRA's latest answer after a check left the service: its analyst's decision on a PEP match,
 * most often (CIRA, 15 Oct 2026: it follows in a second notice). Beside the decision, never in
 * place of it — nothing on this platform changes because of one. Its status and advice are CIRA's
 * own words, verbatim.
 */
export type LaterAnswer = NonNullable<IdentityRecordFacts['laterAnswer']>;

/** The service's own name in a sentence, or what stands for it when there was none. */
export function serviceName(provider: string): string {
  return provider === 'CIRA' ? 'CIRA' : 'the identity service';
}

/** Who checked the document. MANUAL means no service read anything: a person did it all. */
export function providerWords(provider: string): string {
  if (provider === 'CIRA') return 'CIRA, the identity service';
  if (provider === 'MANUAL') return 'A person, by hand — no identity service';
  return codeAsWords(provider);
}

/** A decided check's outcome, in words: the Decided list and every record say it alike. */
export const DECIDED_WORDS: Readonly<Record<string, string>> = {
  VERIFIED: 'Verified',
  REFUSED: 'Refused',
};

/**
 * Where a check stands, for the player it belongs to.
 *
 * A REPLACED attempt that was never decided is history, not something the service is still
 * working on: a record is replaced when the player is given a new live-photo link, or sends new
 * photos after a refusal, and the old one is left as it was.
 */
export function outcomeWords(
  r: Pick<IdentityRecordFacts, 'outcome' | 'current' | 'submittedAt' | 'provider'>,
): string {
  const decided = DECIDED_WORDS[r.outcome];
  if (decided !== undefined) return decided;
  if (!r.current) return 'Not finished';
  if (r.outcome === 'REVIEW') return 'Waiting for a person';
  if (r.submittedAt === null) return 'Photos not sent';
  return r.provider === 'CIRA' ? 'With the identity service' : 'Not yet with a person';
}

/** The KYC chip's tones, so one player reads the same in the header and on the tab. */
export function outcomeTone(outcome: string): TagTone {
  if (outcome === 'VERIFIED') return 'neutral';
  if (outcome === 'REFUSED') return 'quiet';
  return 'outline';
}

/**
 * Where a name or a date of birth came from, or null when nothing is recorded for it.
 *
 * The reviewer is named where the route named the person who decided: they are the only
 * person who can type into a record.
 */
export function sourceWords(
  value: string | null,
  source: IdentitySource,
  r: Pick<IdentityRecordFacts, 'provider' | 'decision'>,
): string | null {
  if (value === null) return null;
  if (source === 'REVIEWER') return `Typed from the ID by ${r.decision?.personName ?? 'the reviewer'}`;
  if (source === 'NOT_RECORDED') {
    return `Not recorded whether ${serviceName(r.provider)} read it or the reviewer typed it`;
  }
  return `Read off the ID by ${serviceName(r.provider)}`;
}

/** The liveness result as a person would say it; the service's own word where it did not pass. */
export function livenessWords(raw: string | null): string {
  if (raw === null) return 'No result recorded';
  const word = raw.toLowerCase();
  if (word === 'passed' || word === 'pass') return 'Passed';
  return `Did not pass — the service said “${raw}”`;
}

/** Who decided: the service on its own, or a member of staff by name. */
export function decisionWho(d: Pick<RecordDecision, 'by' | 'personName'>, provider: string): string {
  if (d.by === 'SERVICE') return `Automatically, by ${serviceName(provider)}`;
  return d.personName ?? 'A member of staff no longer on file';
}

/** The provider's field names in words. Its document-level figure is one figure for the card. */
const CONFIDENCE_WORDS: Readonly<Record<string, string>> = {
  [DOCUMENT_CONFIDENCE_KEY]: 'Whole document',
  full_name: 'Full name',
  first_name: 'First name',
  middle_name: 'Middle name',
  last_name: 'Last name',
  suffix: 'Suffix',
  date_of_birth: 'Date of birth',
  place_of_birth: 'Place of birth',
  address: 'Address',
  nationality: 'Nationality',
  document_type: 'Document type',
  document_number: 'Document number',
  expires_on: 'Expiry date',
};

/** A confidence's field, in words — never as the provider's key. */
export function confidenceLabel(key: string): string {
  return CONFIDENCE_WORDS[key] ?? codeAsWords(key);
}

/**
 * A calendar day, printed without passing through a clock.
 *
 * A date of birth is a day, not an instant: read through the browser's timezone it can land a
 * day early, which is the shift that once made every case read "they disagree". Pinned to UTC
 * midnight and printed in UTC, it cannot move.
 */
const DAY = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
export function calendarDay(ymd: string): string {
  return DAY.format(new Date(`${ymd}T00:00:00Z`));
}

/** An instant, in Manila, with the year — a decision is read long after the day it was made. */
const MOMENT = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'Asia/Manila',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
export function momentWords(iso: string): string {
  return MOMENT.format(new Date(iso));
}

/** The day an instant fell on, in Manila. */
const MANILA_DAY = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'Asia/Manila',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
export function manilaDay(iso: string): string {
  return MANILA_DAY.format(new Date(iso));
}

/**
 * What a record says about photographs it no longer holds.
 *
 * The retention job deletes the photographs of a REPLACED reading once its period has run and
 * keeps the record, which is the evidence that the check happened. A record that never held
 * any says only that.
 */
export function photosGoneWords(imagesPurgedAt: string | null): string {
  return imagesPurgedAt === null
    ? 'This check holds no photographs.'
    : `The photographs were deleted on ${manilaDay(imagesPurgedAt)}, when the retention period ran out. What the check recorded is kept.`;
}

/*
 * ── CIRA'S SCREENING AND ITS ADVICE, IN WORDS ───────────────────────────────
 *
 * CIRA, 15 Oct 2026: screening is switched off for this account (every screening field null);
 * with it on, a sanctions match comes with advice to reject, and a PEP match goes to CIRA's own
 * analyst. Every sentence below is conditioned on the value the route sent: null is said as
 * "not screened", never as "no match", and advice is said in words only for the one word CIRA
 * has documented. Any other is quoted, untranslated.
 */

/** Whether CIRA's advice is to reject: REJECTED, the one advice word CIRA has documented. */
export function advisesRejecting(advice: string | null): boolean {
  return advice !== null && advice.trim().toUpperCase() === 'REJECTED';
}

/** CIRA's advice, in words. */
export function adviceWords(advice: string | null): string {
  if (advice === null) return 'No advice given';
  return advisesRejecting(advice) ? 'Advised rejecting this player' : `“${advice}”, CIRA’s own word`;
}

/**
 * What one check's screening found, list by list — or that nothing screened it at all.
 *
 * On a CIRA check CIRA holds nothing for (`noCiraCheck`), an unanswered list is said as NOT ON
 * RECORD, in the case's own words: CIRA may have screened a check whose reference the platform
 * could not record, so "CIRA did not screen" can be untrue there.
 */
export function screeningWords(
  screening: ScreeningFacts,
  provider: string,
  noCiraCheck = false,
): string {
  if (screening.sanctionsHit === null && screening.pepHit === null) {
    if (noCiraCheck) return 'No sanctions or PEP screening is on record for this check';
    return provider === 'CIRA'
      ? 'Not screened: CIRA did not screen this check for sanctions or PEP'
      : 'Not screened: no identity service screened this check for sanctions or PEP';
  }
  const sanctions =
    screening.sanctionsHit === null
      ? noCiraCheck
        ? 'No screening against the sanctions lists is on record'
        : 'Not screened against the sanctions lists'
      : screening.sanctionsHit
        ? 'Matched a sanctions list'
        : 'No sanctions match';
  const pep =
    screening.pepHit === null
      ? noCiraCheck
        ? 'No screening for politically exposed persons is on record'
        : 'Not screened for politically exposed persons'
      : screening.pepHit
        ? 'Matched a politically exposed person'
        : 'No PEP match';
  return `${sanctions} · ${pep}`;
}

/**
 * What a decision on a check nobody fully screened did NOT do — or null when both lists were
 * screened. A person approving such a check screened no one: there is no sanctions or PEP tool on
 * this platform, and CIRA screens nobody on this account since 15 Oct 2026.
 */
export function unscreenedApprovalWords(
  r: Pick<
    IdentityRecordFacts,
    'outcome' | 'decision' | 'screening' | 'current' | 'noCiraCheckOnRecord'
  >,
): string | null {
  if (r.screening.sanctionsHit !== null && r.screening.pepHit !== null) return null;
  /* A CIRA check CIRA holds nothing for cannot be approved at all (sql/992), so nothing is said
     about what approving it would do — the case says the same. */
  if (r.noCiraCheckOnRecord) return null;
  if (r.outcome === 'VERIFIED' && r.decision?.by === 'PERSON') return 'The approval did not screen the player.';
  /* Only of a check somebody can still approve: a replaced attempt never will be. */
  if (r.outcome === 'REVIEW' && r.decision === null && r.current) return 'Approving it does not screen the player.';
  return null;
}

/**
 * Whether CIRA's later answer disagrees with how the check was decided.
 *
 * The ONE disagreement its documented words can show: advice to reject a verified player. Advice in
 * any other word is not read as approval or refusal — it is shown as CIRA wrote it, beside the
 * decision, for the reader to weigh.
 */
export function laterAnswerDisagrees(r: Pick<IdentityRecordFacts, 'outcome' | 'laterAnswer'>): boolean {
  return r.outcome === 'VERIFIED' && r.laterAnswer !== null && advisesRejecting(r.laterAnswer.advice);
}

/** The disagreement, said plainly — and that it changed nothing. */
export function disagreementWords(r: Pick<IdentityRecordFacts, 'decision'>): string {
  const by =
    r.decision?.by === 'PERSON'
      ? ` by ${r.decision.personName ?? 'a member of staff no longer on file'}`
      : ' automatically';
  return (
    `This player was verified${by}, but CIRA’s later answer advised rejecting them. ` +
    'Nothing changed here because of it: the verification stands.'
  );
}

/** CIRA's later answer, in words: its advice, its screening, and its own status word. */
export function laterAnswerWords(later: LaterAnswer): string {
  return [adviceWords(later.advice), screeningWords(later.screening, 'CIRA'), `CIRA’s status “${later.status}”`].join(
    ' · ',
  );
}
